"use client";

import { useEffect } from "react";

import { reportClientError } from "@/lib/actions/client-error";

/**
 * N10 (Review 2026-07-25): last-resort error boundary.
 *
 * `app/error.tsx` can only catch errors that happen *inside* the root layout.
 * If the root layout itself (or a provider it mounts) throws, Next.js falls
 * back to its own built-in error screen -- English, unstyled and with a stack
 * trace in development. `global-error.tsx` replaces that screen, therefore it
 * has to render its own <html>/<body>.
 *
 * Deliberately self-contained: no design-system components, no Tailwind
 * classes and no external CSS, because the very layout that loads them is the
 * thing that just failed. Only inline styles and one <style> block for the
 * dark-mode variant. No route slugs or technical identifiers are shown.
 */
export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        // Same sink as app/error.tsx: forward the failure into system_logs.
        if (process.env.NODE_ENV === "production") {
            void reportClientError(
                error.message,
                error.digest,
                {
                    route: typeof window !== "undefined" ? window.location.pathname : undefined,
                    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
                },
            ).catch(() => {
                /* swallow: error reporting must never break the boundary */
            });
        }
        console.error("Global error boundary caught:", error);
    }, [error]);

    return (
        <html lang="de">
            <body style={{ margin: 0 }}>
                <style>{`
                    .zp-global-error {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 1rem;
                        background: #ffffff;
                        color: #0a0a0a;
                        font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
                    }
                    .zp-global-error__card {
                        max-width: 32rem;
                        width: 100%;
                        border: 1px solid #e5e5e5;
                        border-radius: 0.75rem;
                        padding: 2rem;
                        text-align: center;
                    }
                    .zp-global-error__button {
                        margin-top: 1.5rem;
                        padding: 0.6rem 1.2rem;
                        min-height: 40px;
                        border-radius: 0.5rem;
                        border: 1px solid #0a0a0a;
                        background: #0a0a0a;
                        color: #ffffff;
                        font-size: 0.95rem;
                        cursor: pointer;
                    }
                    @media (prefers-color-scheme: dark) {
                        .zp-global-error { background: #0a0a0a; color: #fafafa; }
                        .zp-global-error__card { border-color: #262626; }
                        .zp-global-error__button {
                            border-color: #fafafa;
                            background: #fafafa;
                            color: #0a0a0a;
                        }
                    }
                `}</style>
                <div className="zp-global-error">
                    <div className="zp-global-error__card">
                        <h1 style={{ fontSize: "1.5rem", margin: "0 0 0.75rem" }}>
                            Anwendungsfehler
                        </h1>
                        <p style={{ margin: 0, lineHeight: 1.5 }}>
                            Die Anwendung konnte nicht geladen werden. Bitte versuchen Sie es
                            erneut. Bleibt der Fehler bestehen, wenden Sie sich bitte an den
                            Support.
                        </p>
                        <button
                            type="button"
                            className="zp-global-error__button"
                            onClick={() => reset()}
                        >
                            Erneut versuchen
                        </button>
                    </div>
                </div>
            </body>
        </html>
    );
}
